import * as React from "react";
import { RLink } from "./RLink";

type SmallButtonProps = {
  onClick?: () => void;
  children: React.ReactNode;
  variant?: "default" | "active" | "mod" | "blue" | "tertiary" | "primary";
  badge?: number;
  icon?: React.ReactNode;
  /** @deprecated Use variant instead */
  className?: string;
  to?: string;
  active?: boolean;
  testId?: string;
  disabled?: boolean;
};

export const SmallButton = ({
  onClick,
  children,
  variant = "default",
  badge,
  icon,
  className = "",
  to,
  active = false,
  testId,
  disabled = false,
}: SmallButtonProps): JSX.Element => {
  const baseClass =
    "flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium transition no-underline hover:no-underline hover:text-white cursor-pointer";
  const disabledClass = disabled
    ? "opacity-50 cursor-not-allowed pointer-events-none"
    : "";

  const variantClasses = {
    default:
      "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-500",
    tertiary:
      "bg-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300",
    primary:
      "bg-sky-500 text-white hover:bg-sky-600 dark:bg-sky-600 dark:hover:bg-sky-500",
    active:
      "text-red-500 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30",
    mod: "bg-violet-100 text-violet-700 hover:bg-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:hover:bg-violet-900/50",
    blue: active
      ? "bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/30"
      : "bg-transparent text-slate-600 hover:bg-slate-100 dark:bg-transparent dark:text-slate-400 dark:hover:bg-slate-700",
  };

  const combinedClass = `${baseClass} ${variantClasses[variant]} ${disabledClass} ${className}`;

  const content = (
    <>
      {icon}
      <span>{children}</span>
      {badge !== undefined && badge > 0 && (
        <span className="ml-0.5 text-slate-500 dark:text-slate-400">
          {badge}
        </span>
      )}
    </>
  );

  if (to) {
    return (
      <RLink
        to={to}
        onClick={onClick}
        variant="unstyled"
        className={combinedClass}
        data-testid={testId}
      >
        {content}
      </RLink>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={combinedClass}
      data-testid={testId}
    >
      {content}
    </button>
  );
};
